import { FLAG_TYPE_COACH } from "@components/Settings/UserDefinedFlags/constants";
import { getTextColorBasedOnBg } from "@utils/color_utils";
import { isCoachEntry } from "./flags.mapper";

// Neutral stand-in for a flag saved without a color, so a flag is never invisible.
export const FLAG_FALLBACK_COLOR = "#8c8c8c";

// Only a coach flag can be tagged by hand — the backend rejects a logic flag id.
export const selectCoachFlags = (flags) =>
    (flags || []).filter((f) => f.type === FLAG_TYPE_COACH && f.is_active && f.id != null);

// A grid cell key (`${player_id}_${date}`) → the {player_id, date} the API addresses it by.
const cellOf = (key) => {
    const [playerId, date] = key.split("_");
    return { player_id: Number(playerId), date };
};

// The note a cell will be saved with. Its own note wins; a cell with none takes the common
// note; a cell with neither is tagged with no note, since the note is optional.
const noteFor = (key, cellNotes, common) => cellNotes[key]?.trim() || common;

// Diff the dialog against what it loaded, into the two calls a save makes.
//
//   writes  — cells to tag or re-tag: newly checked, or checked before but resolving to a
//             different note now (a common-note edit moves every cell that has no own note).
//             A cell whose note is unchanged is left alone, so its stamps are not rewritten.
//   removes — cells that were loaded checked and are now unchecked. These need the bulk
//             delete's explicit `targets` form: the cartesian form would also clear cells
//             that merely share a player or a date with them.
//
// Unchecking everything yields writes: [] and removes: every loaded cell — a mass removal,
// which stays valid even with a flag picked and note text still in the box.
export const resolveBulkChanges = ({ selected, cellNotes, commonNote, baseline }) => {
    const common = commonNote?.trim() || null;
    const writes = [];
    selected.forEach((key) => {
        const notes = noteFor(key, cellNotes, common);
        const wasSelected = baseline.selected.has(key);
        // `baseline.notes` holds each loaded cell's full note, as it is stored.
        const hadNotes = noteFor(key, baseline.notes, null);
        if (!wasSelected || notes !== hadNotes) writes.push({ ...cellOf(key), notes });
    });
    const removes = [];
    baseline.selected.forEach((key) => {
        if (!selected.has(key)) removes.push(cellOf(key));
    });
    return { writes, removes };
};

// The coach entries already on one cell. A cell can carry several coach flags.
export const coachFlagsOnCell = (cellInfo) => {
    const flags = cellInfo?.flags;
    if (!flags || Array.isArray(flags) || !Array.isArray(flags.user_defined)) return [];
    return flags.user_defined.filter(isCoachEntry);
};

// { uniqueKey: notes | null } for every cell in `keys` that already carries this coach flag.
export const coachTagsFor = (cells, flagId, keys) => {
    const out = {};
    keys.forEach((key) => {
        const hit = coachFlagsOnCell(cells[key]).find((f) => f.user_defined_flag_id === flagId);
        if (hit) out[key] = hit.notes ?? null;
    });
    return out;
};

// The rows of the View only table: one per player, date and coach flag, ordered by date, then the
// players' order, then the order of `coachFlags`. A player-date with no coach flag gets no rows.
// The filter is OR: with flags checked, only the rows of the checked flags show, so a player-date
// shows if it carries any of them; nothing checked means no filter. Only the flags in
// `coachFlags` (the team's active coach flags, the same list as the filter pills) give rows.
export const coachFlagRowsFor = ({ cells, players, dates, coachFlags, checkedIds }) => {
    const order = new Map(coachFlags.map((flag, i) => [flag.id, i]));
    const checked = new Set(checkedIds);
    const shown = (id) => order.has(id) && (checked.size === 0 || checked.has(id));
    const rowsOf = (player, date) =>
        coachFlagsOnCell(cells[`${player.player_id}_${date}`])
            .filter((entry) => shown(entry.user_defined_flag_id))
            .sort((a, b) => order.get(a.user_defined_flag_id) - order.get(b.user_defined_flag_id))
            .map((entry) => ({
                key: `${player.player_id}_${date}_${entry.user_defined_flag_id}`,
                date,
                player,
                flag: coachFlags[order.get(entry.user_defined_flag_id)],
                note: entry.notes || "",
            }));
    return dates.flatMap((date) => players.flatMap((player) => rowsOf(player, date)));
};

const HEX_COLOR = /^#[0-9a-f]{6}$/i;
const SHEET_BORDER = { borderColor: "#D9D9D9", borderStyle: "thin" };

// A spreadsheet fill has no opacity, so the 10% tint is the flag color mixed into white.
const tintOf = (hex) =>
    `#${[1, 3, 5]
        .map((i) => Math.round(255 - (255 - parseInt(hex.slice(i, i + 2), 16)) * 0.1))
        .map((c) => c.toString(16).padStart(2, "0"))
        .join("")}`;

// The View only table as a write-excel-file sheet: the same four columns, each row tinted with its
// flag color, and the Flag cell filled with the full flag color. `rows` are the rows the table
// shows, with the player's display `name`.
export const coachFlagSheetFor = (rows) => {
    const header = ["Date", "Player", "Flag", "Note"].map((value) => ({
        value,
        fontWeight: "bold",
        backgroundColor: "#FAFAFA",
        ...SHEET_BORDER,
    }));
    const body = rows.map(({ date, name, player, flag, note }) => {
        const hasColor = HEX_COLOR.test(flag.color || "");
        const flagColor = hasColor ? flag.color : FLAG_FALLBACK_COLOR;
        const cell = { alignVertical: "top", ...SHEET_BORDER, ...(hasColor && { backgroundColor: tintOf(flag.color) }) };
        return [
            { ...cell, value: date },
            { ...cell, value: `${name} #${player.number}` },
            { ...cell, value: flag.name, backgroundColor: flagColor, textColor: getTextColorBasedOnBg(flagColor) },
            { ...cell, value: note, wrap: true },
        ];
    });
    return {
        data: [header, ...body],
        options: {
            sheet: "Coach flags",
            columns: [{ width: 12 }, { width: 28 }, { width: 18 }, { width: 60 }],
            stickyRowsCount: 1,
        },
    };
};
